import React from 'react';
import { ModelCardProps } from './types';
import styles from '@/styles/components/models-gallery/ModelCard.module.css';

const ModelCard: React.FC<ModelCardProps> = React.memo(({ model, onGoToChat, className, isFocused, onFocus }) => {
  const formatPrice = (pricing: typeof model.pricing) => {
    const { amount, unit } = pricing;
    const unitDisplay = unit === 'per_1m_tokens' ? '/1M tokens' :
      unit === 'per_request' ? '/request' : '/minute';
    return `$${amount}${unitDisplay}`;
  };

  const formatPriceForDisplay = (pricing: typeof model.pricing) => {
    const { amount, unit } = pricing;
    const unitText = unit === 'per_1m_tokens' ? 'per 1M Token' :
      unit === 'per_request' ? 'per request' : 'per minute';
    return { amount: `$${amount}`, unit: unitText };
  };

  const getLicenseIcon = () => {
    return model.license.icon || '/icons/model gallery/license_icon.svg';
  };

  const handleGoToChat = () => {
    if (model.isAvailable) {
      onGoToChat(model.id);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleGoToChat();
    }
  };

  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  const priceDisplay = formatPriceForDisplay(model.pricing);

  return (
    <article
      className={`${styles.cardModel} ${className || ''} ${isFocused ? styles.focused : ''}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      aria-labelledby={`model-${model.id}-title`}
      aria-describedby={`model-${model.id}-description model-${model.id}-pricing`}
    >
      {/* Hidden pricing text for tests */}
      <span className="sr-only">{formatPrice(model.pricing)}</span>

      <div className={styles.autoLayoutVertical}>
        <div className={styles.autoLayoutHorizontal}>
          <div className={styles.sizexsTypeunselected}>
            <img
              className={styles.iconApiDoc}
              alt="License"
              src={getLicenseIcon()}
            />
            <div className={styles.autoLayoutHorizontal1}>
              <div className={styles.text}>{model.license.type}</div>
            </div>
          </div>
          {/* Blockchain NFT indicator */}
          {model.id.startsWith('nft-') && (
            <div className={styles.blockchainBadge} title="Blockchain-verified NFT model">
              <span className={styles.blockchainIcon}>🔗</span>
              <span className={styles.blockchainText}>NFT</span>
            </div>
          )}
          <div className={styles.sizexsTypequaternary}>
            <img className={styles.iconMore} alt="More options" src="/icons/chevron_down.svg" />
          </div>
        </div>
        <h3
          id={`model-${model.id}-title`}
          className={styles.bigTigerGemma}
        >
          {model.name}{model.version && <span> v{model.version}</span>}
        </h3>
      </div>
      <p
        id={`model-${model.id}-description`}
        className={styles.bigTigerGemma1}
      >
        {model.description}
      </p>
      <ul className={styles.autoLayoutHorizontal2} aria-label={`Model tags: ${model.tags.join(', ')}`}>
        {model.tags.slice(0, 3).map((tag) => (
          <li key={tag} className={styles.sizexsTypesecondary}>
            <div className={styles.autoLayoutHorizontal3}>
              <div className={styles.text1}>{tag}</div>
            </div>
          </li>
        ))}
        {model.tags.length > 3 && (
          <li className={styles.sizexsTypesecondary}>
            <div className={styles.autoLayoutHorizontal3}>
              <div className={styles.text1}>+{model.tags.length - 3}</div>
            </div>
          </li>
        )}
      </ul>
      <div className={styles.splitterLine}></div>
      <div className={styles.autoLayoutHorizontal6}>
        <div className={styles.autoLayoutHorizontal7} id={`model-${model.id}-pricing`}>
          <div className={styles.div}>{priceDisplay.amount}</div>
          <div className={styles.autoLayoutHorizontal8}>
            <div className={styles.per1mToken}>{priceDisplay.unit}</div>
            <img className={styles.iconInfo} alt="Pricing info" src="/icons/model gallery/dollar.svg" />
          </div>
        </div>
        <button
          className={styles.sizesmTypesecondaryAcc}
          onClick={handleGoToChat}
          disabled={!model.isAvailable}
          aria-label={`Chat with ${model.name}`}
        >
          <img className={styles.iconChat} alt="Chat" src="/icons/model gallery/chat.svg" />
          <div className={styles.autoLayoutHorizontal3}>
            <div className={styles.text1}>Chat</div>
          </div>
        </button>
      </div>

      {!model.isAvailable && (
        <span
          id={`model-${model.id}-unavailable`}
          className={styles.unavailableText}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          Currently unavailable
        </span>
      )}

      {/* Popularity indicator (visual only) */}
      {model.popularity && model.popularity > 80 && (
        <div className={styles.popularityBadge} aria-hidden="true">
          <span className={styles.popularBadge}>Popular</span>
        </div>
      )}
    </article>
  );
});

export default ModelCard;